<template>
  <svg viewBox="0 0 300 300" style="width: 100%; height: 100%;">

    <!-- 10 equal pie slices -->
    <path
      v-for="(s, i) in pieSlices"
      :key="i"
      :d="s.d"
      :fill="s.fill"
      :stroke="s.stroke"
      stroke-width="0.5"
    />

    <!-- Outer group ring bands -->
    <path
      v-for="(g, i) in groupRings"
      :key="`ring-${i}`"
      :d="g.band"
      fill="white"
      stroke="black"
      stroke-width="0.8"
    />

    <!-- Invisible path guides for textPath -->
    <defs>
      <path
        v-for="(g, i) in groupRings"
        :key="`def-${i}`"
        :id="`pvq-arc-${uid}-${i}`"
        :d="g.textArc"
      />
      <path
        v-for="(s, i) in pieSlices"
        :key="`sdef-${i}`"
        :id="`pvq-rad-${uid}-${i}`"
        :d="s.labelPath"
      />
    </defs>

    <!-- Slice dimension labels -->
    <text
      v-for="(s, i) in pieSlices"
      :key="`slbl-${i}`"
      fill="black"
      font-size="7"
      font-family="sans-serif"
    >
      <textPath
        :href="`#pvq-rad-${uid}-${i}`"
        startOffset="50%"
        text-anchor="middle"
        dominant-baseline="middle"
      >{{ s.label }}</textPath>
    </text>

    <!-- Curved group labels -->
    <text
      v-for="(g, i) in groupRings"
      :key="`lbl-${i}`"
      fill="black"
      font-size="8"
      font-weight="bold"
      font-family="sans-serif"
    >
      <textPath
        :href="`#pvq-arc-${uid}-${i}`"
        startOffset="50%"
        text-anchor="middle"
        dominant-baseline="middle"
      >{{ g.label }}</textPath>
    </text>

    <!-- Needle arrow — angle in degrees clockwise from 12 o'clock -->
    <g v-if="angle != null" :transform="`rotate(${angle}, ${CX}, ${CY})`">
      <!-- Needle body: tip points toward 12 o'clock before rotation -->
      <path
        :d="`M ${CX},${CY - NEEDLE_TIP} L ${CX + NEEDLE_WIDTH},${CY} L ${CX},${CY + NEEDLE_TAIL} L ${CX - NEEDLE_WIDTH},${CY} Z`"
        fill="rgba(255,255,255,0.55)"
        stroke="black"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
      <!-- Pivot cap -->
      <circle :cx="CX" :cy="CY" r="5" fill="rgba(255,255,255,0.55)" stroke="black" stroke-width="1.5" />
    </g>

  </svg>
</template>

<script>
const CX = 150
const CY = 150
const R  = 118   // pie radius

const R_INNER = R
const R_OUTER = R + 24
const R_MID   = (R_INNER + R_OUTER) / 2

const STEP  = (2 * Math.PI) / 10
const START = 0   // 3 o'clock; CCW trig direction (y negated in coord)

// Needle geometry (all in SVG units, measured from centre CX,CY)
const NEEDLE_TIP   = 100  // distance from centre to the pointed tip
const NEEDLE_TAIL  =  14  // distance from centre to the blunt tail
const NEEDLE_WIDTH =   5  // half-width of the diamond at the pivot

const WHEEL_ORDER = [
  'benevolence',    'universalism',               // Self-Transcendence  (0–1)
  'self-direction', 'stimulation', 'hedonism',   // Openness to Change  (2–4)
  'achievement',    'power',                      // Self-Enhancement    (5–6)
  'security',       'tradition',   'conformity',  // Conservation        (7–9)
]

const VALUE_LABELS = {
  'benevolence':    'Bunăvoință',
  'universalism':   'Universalism',
  'self-direction': 'Autodirecționare',
  'stimulation':    'Stimulare',
  'hedonism':       'Hedonism',
  'achievement':    'Realizare',
  'power':          'Putere',
  'security':       'Securitate',
  'conformity':     'Conformitate',
  'tradition':      'Tradiție',
}

const VALUE_COLORS_BG = [
  'rgba( 30, 136, 229, 0.75)',  // benevolence     – blue
  'rgba(  0, 137, 123, 0.75)',  // universalism    – teal
  'rgba( 67, 160,  71, 0.75)',  // self-direction  – green
  'rgba(124, 179,  66, 0.75)',  // stimulation     – light green
  'rgba(255, 179,   0, 0.75)',  // hedonism        – amber
  'rgba(244,  81,  30, 0.75)',  // achievement     – deep orange
  'rgba( 55,  71,  79, 0.75)',  // power           – charcoal
  'rgba(120, 144, 156, 0.75)',  // security        – blue-grey
  'rgba(156,  39, 176, 0.75)',  // tradition       – purple
  'rgba(186, 104, 200, 0.75)',  // conformity      – light purple
]

const VALUE_COLORS_BORDER = [
  'rgba( 30, 136, 229, 1)',
  'rgba(  0, 137, 123, 1)',
  'rgba( 67, 160,  71, 1)',
  'rgba(124, 179,  66, 1)',
  'rgba(255, 179,   0, 1)',
  'rgba(244,  81,  30, 1)',
  'rgba( 55,  71,  79, 1)',
  'rgba(120, 144, 156, 1)',
  'rgba(156,  39, 176, 1)',
  'rgba(186, 104, 200, 1)',
]

const GROUPS = [
  { label: 'Autodepășire',  start: 0, count: 2, color: 'rgba( 13,  71, 161, 0.9)' },  // benevolence, universalism
  { label: 'Deschidere',    start: 2, count: 3, color: 'rgba( 27,  94,  32, 0.9)' },  // self-direction, stimulation, hedonism
  { label: 'Autopromovare', start: 5, count: 2, color: 'rgba( 38,  50,  56, 0.9)' },  // achievement, power
  { label: 'Conservare',    start: 7, count: 3, color: 'rgba( 74,  20, 140, 0.9)' },  // security, tradition, conformity
]

function coord(r, a) {
  return `${(CX + r * Math.cos(a)).toFixed(2)},${(CY - r * Math.sin(a)).toFixed(2)}`
}

// Wedge from center to pie edge
function wedgePath(a0, a1) {
  const large = (a1 - a0) > Math.PI ? 1 : 0
  return `M ${CX},${CY} L ${coord(R, a0)} A ${R},${R} 0 ${large} 0 ${coord(R, a1)} Z`
}

// Annular sector for the ring band
function bandPath(a0, a1) {
  const large = (a1 - a0) > Math.PI ? 1 : 0
  return [
    `M ${coord(R_OUTER, a0)}`,
    `A ${R_OUTER},${R_OUTER} 0 ${large} 0 ${coord(R_OUTER, a1)}`,
    `L ${coord(R_INNER, a1)}`,
    `A ${R_INNER},${R_INNER} 0 ${large} 1 ${coord(R_INNER, a0)}`,
    'Z',
  ].join(' ')
}

// Radial line along the bisector of a slice, used as a textPath guide for the label.
// Left-half slices (cos < 0, angles 90°–270°) are drawn outer→inner so text reads naturally.
function radialPath(mid) {
  const flipped = Math.cos(mid) < 0
  const [r0, r1] = flipped ? [92, 12] : [12, 92]
  return `M ${coord(r0, mid)} L ${coord(r1, mid)}`
}

// Arc at mid-radius used as a textPath guide.
// Upper-half arcs (sin > 0 in trig = visually above center) are reversed so text
// reads L→R from outside the circle. Lower-half arcs go naturally CCW.
function textArcPath(a0, a1) {
  const mid     = (a0 + a1) / 2
  const flipped = Math.sin(mid) > 0
  const [ta0, ta1, sweep] = flipped ? [a1, a0, 1] : [a0, a1, 0]
  const large   = Math.abs(ta1 - ta0) > Math.PI ? 1 : 0
  return `M ${coord(R_MID, ta0)} A ${R_MID},${R_MID} 0 ${large} ${sweep} ${coord(R_MID, ta1)}`
}

export default {
  name: 'PvqWheelChart',

  props: {
    valueRanking: { type: Array, required: true },
    // Degrees clockwise from 12 o'clock. Omit to hide the needle.
    angle: { type: Number, default: null },
  },

  data() {
    return {
      uid: Math.random().toString(36).slice(2),
      CX,
      CY,
      NEEDLE_TIP,
      NEEDLE_TAIL,
      NEEDLE_WIDTH,
    }
  },

  computed: {
    pieSlices() {
      return WHEEL_ORDER.map((v, i) => {
        const a0  = START + i * STEP
        const a1  = a0 + STEP
        const mid = (a0 + a1) / 2
        return {
          d:         wedgePath(a0, a1),
          fill:      VALUE_COLORS_BG[i],
          stroke:    VALUE_COLORS_BORDER[i],
          label:     VALUE_LABELS[v],
          labelPath: radialPath(mid),
        }
      })
    },

    groupRings() {
      return GROUPS.map(g => {
        const a0 = START + g.start * STEP
        const a1 = a0 + g.count * STEP
        return { band: bandPath(a0, a1), textArc: textArcPath(a0, a1), color: g.color, label: g.label }
      })
    },
  },
}
</script>
